import { DidomiSDK } from '@didomi/react';
import { skeletonLoader } from 'app/modules/step-engine/utils/step-engine-methods';
import { getFrontConfiguration, getProductCode } from 'app/shared/reducers/app-management';
import { additionalCheckSSO, blockUserWhenAdditionalFieldsSsoMissed, setSideBarUrls } from 'app/shared/reducers/kubrick-management';
import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from 'react-redux';
import { Router } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import 'reactjs-popup/dist/index.css';
import { SpbLayout } from 'spb-react-components';
import './app.scss';
import Header from './common/mobile/header/header';
import Loader from './common/mobile/loader/loader';
import ModalInfo from './common/mobile/modals/modal-info/modal-info';
import { didomiConfig } from './config/didomi';
import history from './history';
import { getAvatar, loadAppContent, updateAppStylesFromCms } from './modules/cms/service/cms-service';
import { setIsMobile } from './modules/cms/service/store/cms-dispatchs';
import { DeviceType } from './modules/cms/utils/cms-constants';
import { gtmEvent } from './modules/gtm/service/gtm.service';
import { stepEngineApi } from './modules/step-engine/api/step-engine-api';
import { getSpecificStepBehaviors } from './modules/step-engine/service/step-engine-service';
import DisconnectComponent from './modules/step-engine/ui/components/common/disconnect/disconnect-component';
import { ContentType, PagePart } from './modules/step-engine/utils/step-engine-constants';
import Routes from './routes';
import Footer from './shared/components/desktop/footer/footer';
import { MenuItemDTO } from './shared/model/MenuItemDTO.model';
import { IRootState } from './shared/reducers';
import { getAccountSession } from './shared/reducers/authentication';
import { germainApmLoader } from './shared/util/germainApmInit';
import { isMobileDevice, setBarItemsUrl } from './shared/util/method-util';

export const DidomiContext = React.createContext(null);

export const App = (props) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: IRootState) => state.authentication);
  const { deviceType, bankProduct } = useSelector((state: IRootState) => state.appManagement?.frontConfig);
  const { frontConfig, isReady } = useSelector((state: IRootState) => state.appManagement);
  const { stepData } = useSelector((state: IRootState) => state.stepEngine);
  const { claimNumber } = useSelector((state: IRootState) => state.claim);
  const { cmsContent, isMobile } = useSelector((state: IRootState) => state.cms);
  const { activeOutilGermain, monitoringProfilName, appName } = useSelector((state: IRootState) => state.appManagement.frontConfig);
  const { additionalDataSsoIsChecked } = useSelector((state: IRootState) => state.kubrickManagement);
  const [sideBarContent, setSideBarContent] = useState<MenuItemDTO[]>();
  const [sideNavData, setSideNavData] = useState<any>();
  const [footerLinks, setFooterLinks] = useState<any>();
  const [didomiObject, setDidomiObject] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>();
  const { abandonText, abandonTextAfterDeclaration, abandonCancelButton, abandonValidButton } = cmsContent[PagePart.POP_IN]?.prismic || {}
  const { bottomIcon, smallBottomIcon, topIcon, smallTopIcon, items } = cmsContent[PagePart.GENERIC_SIDE_NAV_PDV]?.prismic || {}
  const { backgroundLayoutContainerImage, leftImage, legalNotice, privacyPolicy, termsOfService } = cmsContent[PagePart.APP_GLOBAL_TYPE]?.prismic || {}
  const appGlobalType = cmsContent[PagePart.APP_GLOBAL_TYPE]?.prismic;
  const { verticalDisplay } = useSelector((state: IRootState) => state.appManagement?.frontConfig);
  const isInVerticalDisplay = verticalDisplay && !isMobile;
  const [background, setBackground] = useState('unset');
  const [loadContentLayout, setLoadContentLayout] = useState(false);
  const getContentLayout = () => {
    stepEngineApi.specificStep({ subscriptionDto: stepData.subscriptionDto, requestStep: "LOADING_LAYOUT_CONTENT", deviceType: deviceType || (isMobile ? DeviceType.MOBILE : DeviceType.DESKTOP) }).then(response => {
      const data = setBarItemsUrl(items, response.data?.behaviors.sideBarContent);
      setSideBarContent(data);
      dispatch(setSideBarUrls(response.data?.behaviors.sideBarContent));
      setSideNavData({
        bottomicon: { url: bottomIcon },
        topicon: { url: topIcon },
        smalltopicon: { url: smallTopIcon },
        smallbottomicon: { url: smallBottomIcon },
        items,
      });
      // TODO: update ce qui est commenté, pk ça provient des drools ??
      setFooterLinks({
        [ContentType.LEGAL_NOTICE]: {
          label: legalNotice,
          // url: response.data?.behaviors.footerContent.filter(item => item.label === ContentType.LEGAL_NOTICE)[0].url
          url: "https://assurances.spb.eu/documents/" + frontConfig.productCode + "/LegalNotice.pdf"
        },
        [ContentType.TERMS_OF_SERVICE]: {
          label: termsOfService,
          // url: response.data?.behaviors.footerContent.filter(item => item.label === ContentType.TERMS_OF_SERVICE)[0].url
          url: "https://assurances.spb.eu/documents/" + frontConfig.productCode + "/CGU.pdf"
        },
        [ContentType.PRIVACY_POLICY]: {
          label: privacyPolicy,
          // url: response.data?.behaviors.footerContent.filter(item => item.label === ContentType.PRIVACY_POLICY)[0].url
          url: "https://assurances.spb.eu/documents/" + frontConfig.productCode + "/CharteConfidentialite.pdf"
        }
      });
    }).catch(error => {
      console.log("Error getContentLayout =>", error)
    });
  }

  useEffect(() => {
    dispatch(loadAppContent(PagePart.APP_GLOBAL_TYPE));
    dispatch(getAccountSession());
  }, [])

  useEffect(() => {
    if (appGlobalType) {
      updateAppStylesFromCms(appGlobalType)
    }
  }, [appGlobalType]);

  useEffect(() => {
    dispatch(getFrontConfiguration());
    if (isAuthenticated) {
      dispatch(additionalCheckSSO());
      // dispatch(getSession());
      // dispatch(getWebsiteCode());
    }
    dispatch(getProductCode());
    dispatch(getAvatar());
  }, [isAuthenticated]);

  useEffect(() => {
    if (additionalDataSsoIsChecked === false) {
      dispatch(blockUserWhenAdditionalFieldsSsoMissed())
    }
  }, [additionalDataSsoIsChecked])

  useEffect(() => {
    let timeOut = null;
    const detectDeviceType = () => {
      timeOut = setTimeout(() => {
        if (deviceType) {
          dispatch(setIsMobile(deviceType === DeviceType.MOBILE));
        } else {
          dispatch(setIsMobile(isMobileDevice()));
        }
        const footer: HTMLElement = document.getElementById("footer-container");
        if (footer && backgroundLayoutContainerImage) {
          setBackground(`var(--background-layout-container-image) calc(100% - 1.5rem) calc(100% - 1.5rem - ${footer.offsetHeight}px) / 21% no-repeat fixed`);
        }
      }, 1);
    }
    detectDeviceType();
    window.addEventListener('resize', detectDeviceType);
    return () => {
      clearTimeout(timeOut);
      window.removeEventListener('resize', detectDeviceType);
    };
  }, [deviceType]);

  useEffect(() => {
    if (isMobile !== null) {
      dispatch(loadAppContent(PagePart.POP_IN));
      dispatch(loadAppContent(PagePart.GENERIC_SIDE_NAV_PDV));
      if (isMobile) {
        dispatch(getSpecificStepBehaviors("SIDE_BAR_MOBILE", DeviceType.MOBILE));
      }
    }
  }, [isMobile]);

  useEffect(() => {
    if (activeOutilGermain === true)
      germainApmLoader('https://pacoqqc.cloud.germainapm.com', monitoringProfilName, appName, '')
  }, [activeOutilGermain])

  useEffect(() => {
    // Wait prismic GENERIC_SIDE_NAV_PDV & APP_GLOBAL_TYPE and getContentLayout
    if (!loadContentLayout && legalNotice && ((items && Object.keys(stepData.subscriptionDto).length !== 0) || bankProduct)) {
      setLoadContentLayout(true);
      getContentLayout();
    }
  }, [legalNotice, items, stepData.subscriptionDto]);

  const selectItem = (item) => {
    setSelectedItem(item)
    if (stepData?.dontShowPopupRedirectUrl) {
      dispatch(gtmEvent(item.label, 'Consultation', '0'));
      window.location.href = item.url
    } else {
      setOpen(true)
    }
  }

  const onDidomiReady = didomi => {
    setDidomiObject(didomi);
  };

  const closeModal = () => {
    dispatch(gtmEvent('Abandon', 'Annulé', '0'))
    setOpen(false);
  }

  const confirmModal = () => {
    dispatch(gtmEvent(selectedItem.label, 'Consultation', '0'));
    setOpen(false)
    window.location.href = selectedItem.url
  };

  const showContent = () => {
    return deviceType !== undefined && deviceType !== null && isMobile !== undefined && isMobile !== null;
  }

  return (
    <>
      <Helmet>
        <script src="https://ajax.googleapis.com/ajax/libs/jquery/1.11.1/jquery.min.js"></script>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <title>{appGlobalType?.title || 'Assurances SPB'}</title>
        <meta name="description" content="Rechercher une adhésion et déclarer un sinistre" />
        <meta name="google" content="notranslate" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <meta name="theme-color" content="#000000" />
        <link rel="icon" href="favicon.ico" />
        <link rel="manifest" href="manifest.webapp" />
      </Helmet>
      <DidomiContext.Provider value={didomiObject}>
        <Loader />
        {showContent() &&
          <>
            {isMobile ?
              // MOBILE
              <div className="mobile-view">
                <Router history={history}>
                  <Header didomiObject={didomiObject} footerLinks={footerLinks} sideBarContent={sideBarContent} selectItem={selectItem} />
                  <div className="mobile-page-routes">
                    <div className="mobile-page-content">
                      <Routes />
                    </div>
                  </div>
                </Router>
              </div>
              // MOBILE END
              :
              // DESKTOP
              <>
                <div className="d-flex flex-row">
                  {leftImage &&
                    <div className="left-banner">
                      <img src={leftImage} />
                    </div>
                  }
                  <div className="right-content">
                    {isInVerticalDisplay &&
                      <header className='header-desktop' id="navBar_desktop">
                        <div className="header-toolbar">
                          {sideBarContent || bankProduct ?
                            <>
                              <div className="header-desktop-logo">
                                {topIcon &&
                                  <img className="header-desktop-menu" alt="Logo utilisateur" src={topIcon} />
                                }
                              </div>
                              {!bankProduct &&
                                <>
                                  <div className="header-desktop-content">
                                    {sideBarContent?.map((value, index) => {
                                      return <div className='header-desktop-label' key={index} onClick={() => selectItem(value)}>{value.label}</div>
                                    })}
                                  </div>
                                  <div className="header-desktop-logout">
                                    <DisconnectComponent icon={true} />
                                  </div>
                                </>
                              }
                            </>
                            :
                            skeletonLoader('navbarSkeletonVertical')
                          }
                        </div>
                      </header>
                    }

                    <SpbLayout
                      footer={<Footer didomi={didomiObject} appGlobalType={appGlobalType} footerLinks={footerLinks} />}
                      navbar={{ visible: !isInVerticalDisplay, items: sideBarContent, sidNavData: sideNavData, selectItem }}
                    >

                      <div className={"desktop" + (isInVerticalDisplay ? " vertical" : "")}>
                        <div className="desktop-view">
                          <Router history={history} >
                            <Routes />
                          </Router>
                        </div>
                      </div>
                    </SpbLayout>
                  </div>
                </div>
              </>
            }
            {
              <ModalInfo
                modal={open}
                setModal={setOpen}
                handleOk={confirmModal}
                handleCancel={closeModal}
                message={claimNumber ? [abandonTextAfterDeclaration] : [abandonText]}
                txtValidButton={abandonValidButton}
                txtCancelButton={abandonCancelButton}
              />
            }

            {isReady && frontConfig &&
              <DidomiSDK
                config={didomiConfig(frontConfig.didomiPublicKey, frontConfig.productCode)}
                iabVersion={2} // If you want to support the TCF v1∏, don't forget to change this value, even if you selected the TCF v2 in the console. This parameter will load the correct stub in the React Component
                noticeId={frontConfig.didomiNoticeId} // If you want to target the notice by ID and not by domain
                gdprAppliesGlobally={true}
                sdkPath="https://sdk.privacy-center.org/"
                embedTCFStub={true}
                onReady={onDidomiReady}
                onConsentChanged={cwtToken => console.log('A consent has been given/withdrawn', cwtToken)}
                onNoticeShown={() => console.log('Didomi Notice Shown')}
                onNoticeHidden={() => console.log('Didomi Notice Hidden')}
              />
            }
          </>
        }
      </DidomiContext.Provider>

    </>
  );
};

export default App;
